

import React, { useState, useEffect } from 'react';
import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const HIRING_API = process.env.NODE_ENV === 'production'
  ? `${process.env.REACT_APP_API_URL}/api/hiring-documents`  // ← REPLACE with real backend URL (e.g. )
  : '/api/hiring';

const VEHICLES_API = process.env.NODE_ENV === 'production'
  ? `${process.env.REACT_APP_API_URL}/api/vehicles`   // ← same backend URL
  : '/api/vehicles';
function HiringList() {
  const [hiringVehicles, setHiringVehicles] = useState([]);
  const [originalVehicles, setOriginalVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  // Form state
  const [formData, setFormData] = useState({
    carName: '',
    carNumber: '',
    dailyRate: '',
    monthlyWithDriver: '',
    monthlyWithoutDriver: '',
    yearlyWithoutDriver: '',
    securityDeposit: '',
    originalVehicleId: ''
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [useExistingCar, setUseExistingCar] = useState(true);

  // Booking modals
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showViewBookingsModal, setShowViewBookingsModal] = useState(false);
  const [showEditBookingModal, setShowEditBookingModal] = useState(false);

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [viewingVehicle, setViewingVehicle] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);

  const [bookingData, setBookingData] = useState({
    clientName: '',
    clientPhone: '',
    fromDate: '',
    toDate: ''
  });

  const fetchData = async () => {
  setLoading(true);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const [hiringRes, vehiclesRes] = await Promise.all([
      fetch(HIRING_API, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
      }),
      fetch(VEHICLES_API, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
      })
    ]);

    clearTimeout(timeoutId);

    if (!hiringRes.ok) throw new Error(`Hiring fetch failed: ${hiringRes.status}`);
    if (!vehiclesRes.ok) throw new Error(`Vehicles fetch failed: ${vehiclesRes.status}`);

    const hiringData = await hiringRes.json();
    const vehiclesData = await vehiclesRes.json();

    setHiringVehicles(hiringData.data || []);
    setOriginalVehicles(vehiclesData.data || []);
  } catch (err) {
    console.error(err?.message || 'Unknown error');
    alert('Failed to load data: ' + (err.message || 'Unknown error'));
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    fetchData();
  }, []);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleExistingCarChange = (e) => {
    const selectedId = e.target.value;
    setFormData({ ...formData, originalVehicleId: selectedId });

    if (selectedId) {
      const vehicle = originalVehicles.find(v => v._id === selectedId);
      if (vehicle) {
        setFormData(prev => ({ ...prev, carName: vehicle.name }));
        setImagePreview(vehicle.imageURL);
        setImageFile(null);
      }
    } else {
      setFormData(prev => ({ ...prev, carName: '', originalVehicleId: '' }));
      setImagePreview('');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const openEditForm = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      carName: vehicle.carName,
      carNumber: vehicle.carNumber,
      dailyRate: vehicle.dailyRate,
      monthlyWithDriver: vehicle.monthlyWithDriver,
      monthlyWithoutDriver: vehicle.monthlyWithoutDriver,
      yearlyWithoutDriver: vehicle.yearlyWithoutDriver,
      securityDeposit: vehicle.securityDeposit,
      originalVehicleId: vehicle.originalVehicleId || ''
    });

    if (vehicle.originalVehicleId) {
      setUseExistingCar(true);
      const orig = originalVehicles.find(v => v._id === vehicle.originalVehicleId);
      setImagePreview(orig?.imageURL || vehicle.imageURL);
    } else {
      setUseExistingCar(false);
      setImagePreview(vehicle.imageURL);
    }

    setImageFile(null);
    setShowForm(true);
  };

const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imagePreview) {
      setFormMessage('Please provide an image');
      return;
    }

    setFormLoading(true);
    setFormMessage('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      let imageURL = imagePreview;

      if (imageFile) {
        const imageRef = ref(storage, `hiring/images/${Date.now()}_${imageFile.name}`);
        await uploadBytes(imageRef, imageFile);
        imageURL = await getDownloadURL(imageRef);
      }

      const payload = {
        carName: formData.carName.trim(),
        carNumber: formData.carNumber.trim().toUpperCase(),
        dailyRate: Number(formData.dailyRate),
        monthlyWithDriver: Number(formData.monthlyWithDriver),
        monthlyWithoutDriver: Number(formData.monthlyWithoutDriver),
        yearlyWithoutDriver: Number(formData.yearlyWithoutDriver),
        securityDeposit: Number(formData.securityDeposit),
        imageURL,
        originalVehicleId: formData.originalVehicleId || null
      };

      const res = await fetch(HIRING_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to save hiring vehicle');
      }

      setFormMessage('✅ Hiring vehicle added successfully!');
      resetForm();
      fetchData();
    } catch (err) {
      console.error(err?.message || 'Unknown error');
      setFormMessage('❌ ' + (err.message || 'Failed to save vehicle'));
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      carName: '', carNumber: '', dailyRate: '', monthlyWithDriver: '',
      monthlyWithoutDriver: '', yearlyWithoutDriver: '', securityDeposit: '', originalVehicleId: ''
    });
    setImageFile(null);
    setImagePreview('');
    setUseExistingCar(true);
    setEditingVehicle(null);
    setShowForm(false);
  };

  const handleDelete = async (vehicle) => {
    if (!window.confirm('Delete this hiring vehicle and all its bookings?')) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      if (vehicle.imageURL && !vehicle.originalVehicleId) {
        await deleteObject(ref(storage, vehicle.imageURL)).catch(err => {
          if (err.code !== 'storage/object-not-found') throw err;
        });
      }

      const res = await fetch(`${HIRING_API}/${vehicle._id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) throw new Error('Failed to delete from database');

      fetchData();
      alert('Deleted successfully');
    } catch (err) {
      console.error('Delete failed:', err);
      alert(err?.message || 'Unknown error');
    }
  };
  // Add Booking
  const openBookingModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setBookingData({ clientName: '', clientPhone: '', fromDate: '', toDate: '' });
    setShowBookingModal(true);
  };

  const handleBookingChange = (e) => {
    setBookingData({ ...bookingData, [e.target.name]: e.target.value });
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!bookingData.clientName || !bookingData.clientPhone || !bookingData.fromDate || !bookingData.toDate) {
      alert('Please fill all fields');
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const res = await fetch(`${HIRING_API}/${selectedVehicle._id}/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
        },
        body: JSON.stringify({
          clientName: bookingData.clientName.trim(),
          clientPhone: bookingData.clientPhone.trim(),
          fromDate: bookingData.fromDate,
          toDate: bookingData.toDate
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) throw new Error('Failed to add booking');

      setShowBookingModal(false);
      fetchData();
      alert('Booking added successfully!');
    } catch (err) {
      alert(err.message || 'error');
    }
  };

// Similar updates for saveEditedBooking and deleteBooking (add key + timeout)

  // View Bookings
  const openViewBookingsModal = (vehicle) => {
    setViewingVehicle(vehicle);
    setShowViewBookingsModal(true);
  };

  // Edit Booking
  const openEditBookingModal = (booking) => {
    setEditingBooking(booking);
    setBookingData({
      clientName: booking.clientName,
      clientPhone: booking.clientPhone,
      fromDate: booking.fromDate ? booking.fromDate.split('T')[0] : '',
      toDate: booking.toDate ? booking.toDate.split('T')[0] : ''
    });
    setShowEditBookingModal(true);
    setShowViewBookingsModal(false);
  };

  const saveEditedBooking = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${HIRING_API}/${viewingVehicle._id}/booking/${editingBooking._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName: bookingData.clientName.trim(),
          clientPhone: bookingData.clientPhone.trim(),
          fromDate: bookingData.fromDate,
          toDate: bookingData.toDate
        })
      });

      if (!res.ok) throw new Error('Failed to update');

      setShowEditBookingModal(false);
      setShowViewBookingsModal(true);
      fetchData();
      alert('Booking updated successfully!');
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  // Delete Booking
  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Delete this booking permanently?')) return;

    try {
      const res = await fetch(`${HIRING_API}/${viewingVehicle._id}/booking/${bookingId}`, {
        method: 'DELETE'
      });

      if (!res.ok) throw new Error('Failed to delete');

      fetchData();
      alert('Booking deleted successfully');
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem' }}>
        Loading hiring vehicles...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', color: '#333', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ padding: '4rem 1rem', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h1 style={{ fontSize: '3.5rem', fontWeight: '300', color: '#222' }}>
            Manage <span style={{ fontWeight: '600', color: '#2c5282' }}>Hiring Vehicles</span>
          </h1>
        </div>
{/* Add Hiring Vehicle Button */}
<div style={{ textAlign: 'center', marginBottom: '4rem' }}>
  <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
    <button
      onClick={() => { resetForm(); setShowForm(true); }}
      style={{
        background: '#2c5282',
        color: 'white',
        padding: '1rem 3rem',
        border: 'none',
        borderRadius: '8px',
        fontSize: '1.2rem',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(44,82,130,0.3)',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => e.target.style.transform = 'translateY(-3px)'}
      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
    >
      ➕ Add Hiring Vehicle
    </button>
    
    <button
      onClick={() => window.location.href = '/admin-hiring-content'}
      style={{
        background: '#2c5282',
        color: 'white',
        padding: '1rem 3rem',
        border: 'none',
        borderRadius: '8px',
        fontSize: '1.2rem',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(44,82,130,0.3)',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => e.target.style.transform = 'translateY(-3px)'}
      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
    >
      📋 Add Hiring Notes + Agreement
    </button>
    
  </div>
</div>

        {/* Add/Edit Vehicle Form */}
        {showForm && (
          <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', marginBottom: '4rem' }}>
            <h2 style={{ textAlign: 'center', color: '#2c5282', marginBottom: '2rem', fontSize: '2rem' }}>
              {editingVehicle ? 'Edit Hiring Vehicle' : 'Add New Hiring Vehicle'}
            </h2>

            {formMessage && (
              <div style={{
                padding: '1rem',
                marginBottom: '1.5rem',
                borderRadius: '8px',
                background: formMessage.startsWith('✅') ? '#d4edda' : '#f8d7da',
                color: formMessage.startsWith('✅') ? '#155724' : '#721c24',
                textAlign: 'center'
              }}>
                {formMessage}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Mode</label>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <label>
                    <input type="radio" checked={useExistingCar} onChange={() => setUseExistingCar(true)} />
                    Use Existing Vehicle
                  </label>
                  <label>
                    <input type="radio" checked={!useExistingCar} onChange={() => setUseExistingCar(false)} />
                    Create New
                  </label>
                </div>
              </div>

              {useExistingCar ? (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Select Existing Vehicle *</label>
                  <select
                    value={formData.originalVehicleId}
                    onChange={handleExistingCarChange}
                    required
                    style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem' }}
                  >
                    <option value="">-- Choose a vehicle --</option>
                    {originalVehicles.map(v => (
                      <option key={v._id} value={v._id}>
                        {v.name} ({v.status})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Car Name *</label>
                  <input
                    type="text"
                    name="carName"
                    value={formData.carName}
                    onChange={handleFormChange}
                    required
                    style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem' }}
                  />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Car Number *</label>
                  <input
                    type="text"
                    name="carNumber"
                    value={formData.carNumber}
                    onChange={handleFormChange}
                    required
                    placeholder="e.g. BP-1-PA-1234"
                    style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc', fontSize: '1rem' }}
                  />
                </div>

                {!useExistingCar && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Image *</label>
                    <input type="file" accept="image/*" onChange={handleImageChange} required={!editingVehicle} />
                  </div>
                )}
              </div>

              {imagePreview && (
                <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                  <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Daily Rate (Nu.) *</label>
                  <input type="number" name="dailyRate" value={formData.dailyRate} onChange={handleFormChange} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Monthly (With Driver) *</label>
                  <input type="number" name="monthlyWithDriver" value={formData.monthlyWithDriver} onChange={handleFormChange} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Monthly (Without Driver) *</label>
                  <input type="number" name="monthlyWithoutDriver" value={formData.monthlyWithoutDriver} onChange={handleFormChange} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Yearly (Without Driver) *</label>
                  <input type="number" name="yearlyWithoutDriver" value={formData.yearlyWithoutDriver} onChange={handleFormChange} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Security Deposit *</label>
                  <input type="number" name="securityDeposit" value={formData.securityDeposit} onChange={handleFormChange} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button
                  type="submit"
                  disabled={formLoading}
                  style={{
                    padding: '1rem 3rem',
                    background: '#2c5282',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: '1.1rem'
                  }}
                >
                  {formLoading ? 'Saving...' : editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '1rem 3rem',
                    background: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Vehicle Grid */}
        {hiringVehicles.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '2rem' }}>
            {hiringVehicles.map((hv) => (
              <div
                key={hv._id}
                onMouseEnter={() => setHoveredCard(hv._id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: hoveredCard === hv._id ? '0 20px 40px rgba(0,0,0,0.15)' : '0 8px 25px rgba(0,0,0,0.08)',
                  transition: 'all 0.4s ease',
                  transform: hoveredCard === hv._id ? 'translateY(-10px)' : 'translateY(0)'
                }}
              >
                <div style={{ height: '280px', overflow: 'hidden' }}>
                  <img
                    src={hv.imageURL}
                    alt={hv.carName}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.6s ease',
                      transform: hoveredCard === hv._id ? 'scale(1.1)' : 'scale(1)'
                    }}
                  />
                </div>

                <div style={{ padding: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.6rem', fontWeight: '600', marginBottom: '0.5rem' }}>{hv.carName}</h3>
                  <p style={{ color: '#2c5282', fontWeight: '600' }}>{hv.carNumber}</p>

                  <div style={{ margin: '1rem 0', fontSize: '0.9rem', color: '#555' }}>
                    <p><strong>Daily:</strong> Nu. {hv.dailyRate.toLocaleString()}</p>
                    <p><strong>Security Deposit:</strong> Nu. {hv.securityDeposit.toLocaleString()}</p>
                  </div>

                  {hv.bookings?.length > 0 && (
                    <p style={{ background: '#e6f4ff', padding: '0.5rem', borderRadius: '8px', textAlign: 'center', color: '#2c5282', fontWeight: 'bold', marginBottom: '1rem' }}>
                      {hv.bookings.length} Booking{hv.bookings.length > 1 ? 's' : ''}
                    </p>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                    <button onClick={() => openEditForm(hv)} style={{ padding: '0.8rem', background: '#3182ce', color: 'white', border: 'none', borderRadius: '8px' }}>Edit</button>
                    <button onClick={() => openBookingModal(hv)} style={{ padding: '0.8rem', background: '#28a745', color: 'white', border: 'none', borderRadius: '8px' }}>Add Booking</button>
                    <button onClick={() => openViewBookingsModal(hv)} style={{ padding: '0.8rem', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px' }}>View Bookings</button>
                    <button onClick={() => handleDelete(hv)} style={{ padding: '0.8rem', background: '#e53e3e', color: 'white', border: 'none', borderRadius: '8px' }}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {hiringVehicles.length === 0 && !showForm && (
          <div style={{ textAlign: 'center', padding: '8rem 2rem', color: '#888' }}>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: '#444' }}>No hiring vehicles yet</h2>
            <p style={{ fontSize: '1.3rem' }}>Click the button above to add your first vehicle for hiring</p>
          </div>
        )}

        {/* Add Booking Modal */}
        {showBookingModal && selectedVehicle && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', width: '90%', maxWidth: '500px' }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#2c5282', textAlign: 'center' }}>
                Add Booking for {selectedVehicle.carName}
              </h3>
              <form onSubmit={submitBooking}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Client Name *</label>
                  <input type="text" name="clientName" value={bookingData.clientName} onChange={handleBookingChange} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Client Phone *</label>
                  <input type="tel" name="clientPhone" value={bookingData.clientPhone} onChange={handleBookingChange} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>From Date *</label>
                    <input type="date" name="fromDate" value={bookingData.fromDate} onChange={handleBookingChange} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>To Date *</label>
                    <input type="date" name="toDate" value={bookingData.toDate} onChange={handleBookingChange} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button type="submit" style={{ flex: 1, padding: '1rem', background: '#2c5282', color: 'white', border: 'none', borderRadius: '8px' }}>Confirm</button>
                  <button type="button" onClick={() => setShowBookingModal(false)} style={{ flex: 1, padding: '1rem', background: '#777', color: 'white', border: 'none', borderRadius: '8px' }}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Bookings Modal */}
        {showViewBookingsModal && viewingVehicle && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', width: '90%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#2c5282', textAlign: 'center' }}>
                Bookings for {viewingVehicle.carName}
              </h3>

              {viewingVehicle.bookings && viewingVehicle.bookings.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {viewingVehicle.bookings.map((b) => (
                    <div key={b._id} style={{ padding: '1.5rem', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <strong style={{ fontSize: '1.2rem' }}>{b.clientName}</strong><br />
                          <span style={{ color: '#666' }}>Phone: {b.clientPhone}</span><br />
                          From: <strong>{formatDate(b.fromDate)}</strong> → To: <strong>{formatDate(b.toDate)}</strong>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button onClick={() => openEditBookingModal(b)} style={{ padding: '0.6rem 1rem', background: '#3182ce', color: 'white', border: 'none', borderRadius: '6px' }}>
                            Edit
                          </button>
                          <button onClick={() => deleteBooking(b._id)} style={{ padding: '0.6rem 1rem', background: '#e53e3e', color: 'white', border: 'none', borderRadius: '6px' }}>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#888' }}>No bookings yet.</p>
              )}

              <button
                onClick={() => setShowViewBookingsModal(false)}
                style={{ marginTop: '2rem', width: '100%', padding: '1rem', background: '#666', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Edit Booking Modal */}
        {showEditBookingModal && editingBooking && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '16px', width: '90%', maxWidth: '500px' }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#2c5282' }}>Edit Booking</h3>
              <form onSubmit={saveEditedBooking}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Client Name</label>
                  <input type="text" value={bookingData.clientName} onChange={(e) => setBookingData({ ...bookingData, clientName: e.target.value })} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Client Phone</label>
                  <input type="tel" value={bookingData.clientPhone} onChange={(e) => setBookingData({ ...bookingData, clientPhone: e.target.value })} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>From Date</label>
                    <input type="date" value={bookingData.fromDate} onChange={(e) => setBookingData({ ...bookingData, fromDate: e.target.value })} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>To Date</label>
                    <input type="date" value={bookingData.toDate} onChange={(e) => setBookingData({ ...bookingData, toDate: e.target.value })} required style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: '1px solid #ccc' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button type="submit" style={{ flex: 1, padding: '1rem', background: '#2c5282', color: 'white', border: 'none', borderRadius: '8px' }}>Save</button>
                  <button type="button" onClick={() => { setShowEditBookingModal(false); setShowViewBookingsModal(true); }} style={{ flex: 1, padding: '1rem', background: '#666', color: 'white', border: 'none', borderRadius: '8px' }}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HiringList;
